/**
 * B1020 — the `/gift-cards` controller: whose page (`?tenant=` or a site
 * key), the venue's facts (one `session.guest` read, like /memberships), then
 * the TAB's guest session — the same token and cartId the tickets page keeps
 * in sessionStorage, so a card lands on the cart the tickets are on and they
 * pay together — `giftcard.offer`, and the cart's own read. ADD TO CART
 * opens the cart when there is none (`cart.create`) and asks
 * `cart.gift_card.add`; the door's refusal comes back in its own words.
 * B1020 (2b) — `?edit=<lineId>` (a cart row's EDIT) loads that card from
 * the cart's own read and the door SAVES it in place (`cart.gift_card.update`),
 * then opens the cart. A line the cart no longer holds is the plain form.
 */
import { useCallback, useEffect, useMemo, useState, type ReactElement } from "react";
import { useDocumentTitle } from "@transact/ui";
import { ApiError, createApiClient, type ApiClient } from "../lib/api.js";
import { resolveSiteKey } from "../lib/boot.js";
import { GUEST_DOOR } from "../lib/refusals.js";
import { createGuestGate, type GuestGate } from "../lib/guest.js";
import { meVenueFacts, type MeVenueFacts } from "../lib/me.js";
import { venueBrand } from "../lib/membershipsPage.js";
import { formatMoney } from "../lib/money.js";
import { paintRememberedTheme, paintVenueTheme } from "../lib/paintVenueTheme.js";
import { createSession, type Session, type StorageLike } from "../lib/session.js";
import {
  addBody,
  cartFacts,
  draftFromCard,
  recallCopies,
  updateBody,
  cartFullWhy,
  cartNote,
  draftAmount,
  draftIssues,
  freshDraft,
  offerFrom,
  rememberCopy,
  ticketsHref,
  balanceHref,
  todayIn,
  type CartFacts,
  type DraftIssues,
  type GiftCardDraft,
  type GiftCardOffer,
  type GiftCardsRoute,
} from "../lib/giftCardsPage.js";
import { cartOpenHref } from "../lib/giftCardCartFace.js";
import { GiftCardsPage, type GiftCardsState } from "./giftCardsPage.js";

const LOAD_REFUSED = "Gift cards could not be loaded just now. Try again in a moment.";

const ADD_FAILED = "The card wasn't added just now — nothing changed in your cart. Press Add to cart again.";
const NO_CART: CartFacts = { cards: 0, lines: 0 };

function tabStorage(): StorageLike | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function GiftCardsApp(props: {
  readonly route: GiftCardsRoute;
  readonly apiBaseUrl: string;
  /** Test seams: the API client for a tenant over the tab's session, the tab's storage, the clock. */
  readonly client?: (tenantId: string, session: Session) => ApiClient;
  readonly storage?: StorageLike | null;
  readonly now?: () => number;
  /** Test seam: where a saved edit goes (the cart); the browser's own navigation by default. */
  readonly go?: (href: string) => void;
}): ReactElement {
  const now = props.now ?? Date.now;
  const store = props.storage !== undefined ? props.storage : tabStorage();
  const session = useMemo(() => createSession(store), [store]);
  const [scope, setScope] = useState<{ readonly tenantId: string; readonly channelId: string } | undefined | "unknown">(() =>
    props.route.tenantId !== undefined ? { tenantId: props.route.tenantId, channelId: "web" } : props.route.siteKey !== undefined ? undefined : "unknown",
  );
  useEffect(() => {
    if (scope !== undefined || props.route.siteKey === undefined) return;
    void resolveSiteKey(props.apiBaseUrl, props.route.siteKey).then((ch) => setScope(ch ?? "unknown"));
  }, [scope, props.route.siteKey, props.apiBaseUrl]);
  const tenantId = typeof scope === "object" ? scope.tenantId : undefined;

  const api = useMemo(
    () =>
      tenantId === undefined
        ? undefined
        : (props.client?.(tenantId, session) ?? createApiClient({ baseUrl: props.apiBaseUrl, tenantId, token: () => session.token() })),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the seam is fixed for the page's life
    [tenantId, session, props.apiBaseUrl],
  );
  const gate = useMemo<GuestGate | undefined>(
    () => (api === undefined || typeof scope !== "object" ? undefined : createGuestGate({ api, session, channelId: scope.channelId })),
    [api, session, scope],
  );

  const [venue, setVenue] = useState<MeVenueFacts>({});
  const [offer, setOffer] = useState<GiftCardOffer | undefined>(undefined);
  const [cart, setCart] = useState<CartFacts>(NO_CART);
  const [failed, setFailed] = useState<false | "retry" | "door">(false);
  const [attempt, setAttempt] = useState(0);
  const [draft, setDraft] = useState<GiftCardDraft | undefined>(undefined);
  const [pressed, setPressed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState<{ readonly tone: "success" | "danger"; readonly text: string } | undefined>(undefined);
  /* B1020 (2b) — the cart line this form is editing, once the cart's read holds it; undefined = adding. */
  const [editing, setEditing] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (typeof document !== "undefined") paintRememberedTheme();
  }, []);

  const readCart = useCallback(async (): Promise<unknown> => {
    const cartId = session.cartId();
    if (api === undefined || cartId === undefined) {
      setCart(NO_CART);
      return undefined;
    }
    try {
      const res = await api.call("cart.get", { cartId });
      setCart(cartFacts(res));
      return res;
    } catch (err) {
      /* a cart the server no longer holds (TTL'd, checked out) is forgotten — the next card opens a fresh one */
      if (err instanceof ApiError && (err.status === 404 || err.status === 409)) session.clearCartId();
      setCart(NO_CART);
      return undefined;
    }
  }, [api, session]);

  useEffect(() => {
    if (api === undefined || gate === undefined || typeof scope !== "object") return;
    let gone = false;
    setFailed(false);
    void (async () => {
      /* B641 (review of 4b9dff25): the effect's `venue` is the closure's first render ({}), so the
         day and the EDIT draft were cut in the BROWSER's zone — a buyer a few hours east of the
         business, opening EDIT on a 9:00 PST card and pressing Save untouched, moved its day. The
         zone the read just answered is the one that cuts them. */
      let timeZone: string | undefined;
      try {
        /* the venue's facts ride a guest read of their own (the tab's token may predate them) */
        const anon = props.client?.(scope.tenantId, createSession(null)) ?? createApiClient({ baseUrl: props.apiBaseUrl, tenantId: scope.tenantId });
        const facts = meVenueFacts(await anon.call("session.guest", { channelId: scope.channelId }).catch(() => ({})));
        if (gone) return;
        timeZone = facts.timezone;
        setVenue(facts);
        if (typeof document !== "undefined") paintVenueTheme(facts.theme);
      } catch {
        /* the bar falls back to the tenant's own id */
      }
      try {
        await gate.ensure();
        const o = offerFrom(await api.call("giftcard.offer", {}));
        if (gone) return;
        setOffer(o);
        const res = await readCart();
        if (gone) return;
        const day = todayIn(now(), timeZone);
        const loaded = props.route.edit !== undefined ? draftFromCard(res, props.route.edit, o, timeZone, recallCopies(store), day) : undefined;
        if (loaded !== undefined) setEditing(props.route.edit);
        setDraft((d) => d ?? loaded ?? freshDraft(o, day));
      } catch (err) {
        if (!gone) setFailed(err instanceof ApiError && (err.status === 401 || err.status === 403) ? "door" : "retry");
      }
    })();
    return () => {
      gone = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one load per scope + attempt
  }, [api, gate, scope, attempt]);

  const brand = venueBrand(venue, tenantId, { doorShut: failed === "door" });
  useDocumentTitle(brand.businessName !== undefined ? `Gift cards — ${brand.businessName}` : "Gift cards");
  const today = todayIn(now(), venue.timezone);
  const issues: DraftIssues = draft !== undefined && offer !== undefined && pressed ? draftIssues(draft, offer, today) : {};

  const add = useCallback(async (): Promise<void> => {
    if (api === undefined || offer === undefined || draft === undefined || busy) return;
    setPressed(true);
    setBanner(undefined);
    const found = draftIssues(draft, offer, today);
    /* the refused fields say why; installRefusalFocus (main.tsx) hands the keyboard to the first */
    if (Object.keys(found).length > 0) return;
    setBusy(true);
    try {
      await gate?.ensure();
      let cartId = session.cartId();
      if (cartId === undefined) {
        cartId = `cart-${crypto.randomUUID()}`;
        await api.call("cart.create", { cartId, channelId: typeof scope === "object" ? scope.channelId : "web", currency: offer.currency });
        session.setCartId(cartId);
      }
      if (editing !== undefined) {
        await api.call("cart.gift_card.update", updateBody(draft, offer, cartId, editing, venue.timezone));
        rememberCopy(store, editing, draft.copyToMe);
        if (tenantId !== undefined) (props.go ?? ((href: string) => window.location.assign(href)))(cartOpenHref(tenantId));
        return;
      }
      const res = (await api.call("cart.gift_card.add", addBody(draft, offer, cartId, venue.timezone))) as { lineId?: unknown };
      if (typeof res.lineId === "string") rememberCopy(store, res.lineId, draft.copyToMe);
      const amount = draftAmount(draft, offer) ?? 0;
      setBanner({ tone: "success", text: `Added a ${formatMoney({ amount, currency: offer.currency })} gift card for ${draft.recipientName.trim()}. Add the next person's, or check out.` });
      /* the next card starts fresh for a new person; the sender, design and amount stay */
      setDraft({ ...draft, recipientName: "", recipientEmail: "", message: "" });
      setPressed(false);
      await readCart();
    } catch (err) {
      const said = err instanceof ApiError && err.message.trim() !== "" ? err.message : ADD_FAILED;
      setBanner({ tone: "danger", text: said });
    } finally {
      setBusy(false);
    }
  }, [api, offer, draft, busy, today, gate, session, scope, venue.timezone, store, readCart, editing, tenantId, props.go]);

  const state: GiftCardsState =
    scope === "unknown"
      ? { kind: "error", message: "This page doesn't know whose gift cards to show. Open it from the business's own site.", retry: false }
      : failed === "door"
        ? { kind: "error", message: GUEST_DOOR, retry: false }
        : failed === "retry"
          ? { kind: "error", message: LOAD_REFUSED, retry: true }
          : offer === undefined || draft === undefined
            ? { kind: "loading" }
            : offer.enabled
              ? { kind: "ready", offer }
              : { kind: "off" };
  const tickets = tenantId !== undefined ? ticketsHref(tenantId) : undefined;
  return (
    <GiftCardsPage
      venue={brand}
      state={state}
      ticketsUrl={tickets}
      balanceUrl={tenantId !== undefined ? balanceHref(tenantId) : undefined}
      cartLines={cart.lines}
      onRetry={() => setAttempt((a) => a + 1)}
      form={
        draft !== undefined && offer !== undefined
          ? {
              draft,
              today,
              issues,
              onDraft: setDraft,
              onAdd: () => void add(),
              busy,
              cartNote: cartNote(cart),
              fullWhy: cartFullWhy(cart, offer),
              checkoutUrl: tenantId !== undefined ? cartOpenHref(tenantId) : undefined,
              editing: editing !== undefined,
              banner,
            }
          : undefined
      }
    />
  );
}
